import { Skeleton } from "@/components/ui/skeleton";

interface FontListProps {
  fonts: string[];
  popularFonts?: string[];
  selectedFont: string;
  loadedFonts: Set<string>;
  visibleCount: number;
  isCompact?: boolean;
  maxHeight?: string;
  onFontSelect: (font: string) => void;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
  onRequestFontFace: (font: string) => void;
  ensureGoogleFontLoaded: (font: string) => void;
}

const FontList = ({ 
  fonts, 
  popularFonts = [], 
  selectedFont, 
  loadedFonts, 
  visibleCount,
  isCompact = false,
  maxHeight = "300px",
  onFontSelect, 
  onScroll,
  onRequestFontFace,
  ensureGoogleFontLoaded
}: FontListProps) => {
  const displayedFonts = fonts.slice(0, visibleCount);

  return (
    <div 
      className={`overflow-auto space-y-1`}
      style={{ maxHeight }}
      onScroll={onScroll}
    >
      {/* Popular fonts section */}
      {popularFonts.length > 0 && (
        <div className="mb-2">
          <div className="px-1 pb-1 text-[11px] text-gray-500">Popular</div>
          {popularFonts.map((font) => {
            const isLoaded = loadedFonts.has(font);
            if (!isLoaded) onRequestFontFace(font);
            const isSelected = selectedFont === font;
            
            if (isCompact) {
              // Compact version for sidebar
              return (
                <button
                  key={`popular-${font}`}
                  className={`w-full text-left px-2 py-2 rounded text-xs border transition-all ${
                    isSelected 
                      ? 'bg-blue-50 border-blue-300' 
                      : 'border-transparent hover:bg-gray-50'
                  }`}
                  style={isLoaded ? { fontFamily: font } : undefined}
                  onClick={() => {
                    ensureGoogleFontLoaded(font);
                    onFontSelect(font);
                  }}
                >
                  {isLoaded ? (
                    <div>
                      <div className="font-medium text-gray-900 truncate">{font}</div>
                      <div className="text-gray-500 truncate" style={{ fontFamily: font }}>
                        Sample text
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Skeleton className="h-3 w-20" />
                      <Skeleton className="h-2 w-24" />
                    </div>
                  )}
                </button>
              );
            } else {
              // Full version for overlay panel
              return (
                <button
                  key={`popular-${font}`}
                  className={`w-full text-left px-3 py-3 rounded-lg border transition-all duration-200 group ${
                    isSelected 
                      ? 'bg-blue-50 border-blue-500 shadow-sm' 
                      : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
                  }`}
                  style={isLoaded ? { fontFamily: font } : undefined}
                  onClick={() => {
                    ensureGoogleFontLoaded(font);
                    onFontSelect(font);
                  }}
                >
                  <div className="flex items-center gap-2">
                    {isSelected && (
                      <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      {isLoaded ? (
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-gray-900 truncate">{font}</span>
                          <span 
                            className="text-xs text-gray-500 truncate mt-0.5" 
                            style={{ fontFamily: font }}
                          >
                            The quick brown fox jumps
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          <Skeleton className="h-[14px] w-[120px]" />
                          <Skeleton className="h-[12px] w-[140px]" />
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              );
            }
          })}
          <div className="mt-2 h-px bg-border" />
        </div>
      )}

      {/* Regular fonts list */}
      {displayedFonts.map((font) => {
        const isLoaded = loadedFonts.has(font);
        if (!isLoaded) onRequestFontFace(font);
        const isSelected = selectedFont === font;
        
        if (isCompact) {
          // Compact version for sidebar
          return (
            <button
              key={font}
              className={`w-full text-left px-2 py-2 rounded text-xs border transition-all ${
                isSelected 
                  ? 'bg-blue-50 border-blue-300' 
                  : 'border-transparent hover:bg-gray-50'
              }`}
              style={isLoaded ? { fontFamily: font } : undefined}
              onClick={() => {
                ensureGoogleFontLoaded(font);
                onFontSelect(font);
              }}
            >
              {isLoaded ? (
                <div>
                  <div className="font-medium text-gray-900 truncate">{font}</div>
                  <div className="text-gray-500 truncate" style={{ fontFamily: font }}>
                    Sample text
                  </div>
                </div>
              ) : (
                <div className="space-y-1">
                  <Skeleton className="h-3 w-20" />
                  <Skeleton className="h-2 w-24" />
                </div>
              )}
            </button>
          );
        } else {
          // Full version for overlay panel
          return (
            <button
              key={font}
              className={`w-full text-left px-3 py-3 rounded-lg border transition-all duration-200 group ${
                isSelected 
                  ? 'bg-blue-50 border-blue-500 shadow-sm' 
                  : 'border-transparent hover:bg-gray-50 hover:border-gray-200'
              }`}
              style={isLoaded ? { fontFamily: font } : undefined}
              onClick={() => {
                ensureGoogleFontLoaded(font);
                onFontSelect(font);
              }}
            >
              <div className="flex items-center gap-2">
                {isSelected && (
                  <div className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  {isLoaded ? (
                    <div className="flex flex-col">
                      <span className="text-sm font-medium text-gray-900 truncate">{font}</span>
                      <span 
                        className="text-xs text-gray-500 truncate mt-0.5" 
                        style={{ fontFamily: font }}
                      >
                        The quick brown fox jumps
                      </span>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <Skeleton className="h-[14px] w-[120px]" />
                      <Skeleton className="h-[12px] w-[140px]" />
                    </div>
                  )}
                </div>
              </div>
            </button>
          );
        }
      })}
    </div>
  );
};

export default FontList;
